import React, { useEffect, useState } from "react";
import { Timer, Loader2, Play, Eye, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { fmtWhen } from "@/lib/activityFmt";

// Lot 765 — suppression automatique après N jours d'inactivité (0 = jamais) : interrupteur + N, avertissement
// (cloche + push + e-mail) 7 jours avant, aperçu du prochain passage (rien n'est envoyé) et passage immédiat.
const DISABLED_LABEL = { guard: "garde-fou coupé", switch: "interrupteur éteint", zero_days: "N = 0 (jamais)" };

export const sweepSummary = (s) => {
  if (!s) return "Aucun passage pour l'instant.";
  if (s.aborted) return `Passage annulé par le garde-fou : ${s.aborted}`;
  if (s.disabled) return `Passage sans effet (${DISABLED_LABEL[s.disabled] || s.disabled}).`;
  return `${s.warned} averti(s) · ${s.deleted} supprimé(s) · ${s.cancelled} revenu(s) (avertissement levé) · ${s.skipped_protected} protégé(s) ignoré(s)`;
};

export const InactiveAutoDeleteCard = ({ isSuper, config, onConfig }) => {
  const [enabled, setEnabled] = useState(!!config.enabled);
  const [daysStr, setDaysStr] = useState(String(config.auto_delete_days ?? 0));
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => { setEnabled(!!config.enabled); setDaysStr(String(config.auto_delete_days ?? 0)); }, [config.enabled, config.auto_delete_days]);
  const n = Math.max(0, Math.min(3650, parseInt(daysStr, 10) || 0));
  const dirty = enabled !== !!config.enabled || n !== (config.auto_delete_days ?? 0);
  const warnAt = Math.max(0, n - (config.warn_days || 7));

  const save = async () => {
    setBusy("save");
    try { const { data } = await api.put("/admin/activity/config", { enabled, auto_delete_days: n }); onConfig(data); toast.success(enabled && n > 0 ? `Suppression automatique activée : ${n} jours` : "Suppression automatique désactivée"); }
    catch (e) { toast.error(e?.friendlyMessage || "Enregistrement impossible"); }
    setBusy(null);
  };
  const sweep = async (dry) => {
    if (!dry && !window.confirm("Lancer le passage maintenant ? Les avertissements partent réellement et les comptes dont le délai est écoulé sont supprimés.")) return;
    setBusy(dry ? "preview" : "run");
    try {
      const { data } = await api.post(`/admin/activity/sweep?dry_run=${dry ? 1 : 0}`);
      setPreview({ ...data, dry });
      if (!dry) { const { data: cfg } = await api.get("/admin/activity/config"); onConfig(cfg); toast.success("Passage terminé"); }
    } catch (e) { toast.error(e?.friendlyMessage || "Passage impossible"); }
    setBusy(null);
  };

  return (
    <div className="yalola-card p-4" data-testid="autodelete-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Timer size={18} style={{ color: "var(--yalola-primary)" }} />
          <div>
            <div className="font-bold font-display">Suppression automatique des comptes inactifs</div>
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Passage quotidien (04:10 UTC). Avertissement {config.warn_days || 7} jours avant (cloche + notification push, e-mail si le membre en a un), puis suppression définitive si aucune reconnexion.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold" data-testid="autodelete-state">{enabled && n > 0 ? "Activée" : "Désactivée"}</span>
          <Switch checked={enabled} onCheckedChange={setEnabled} disabled={!isSuper} data-testid="autodelete-switch" />
        </div>
      </div>
      {!config.guard_on && (
        <div className="mt-3 rounded-xl px-3 py-2 text-xs flex items-center gap-2" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="autodelete-guard-off">
          <AlertTriangle size={14} /> Garde-fou « inactive_accounts » coupé dans Paramètres › Garde-fous : le passage quotidien est neutralisé quel que soit le réglage ci-dessous.
        </div>
      )}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="text-sm">Supprimer après</span>
        <Input type="number" min={0} max={3650} value={daysStr} onChange={(e) => setDaysStr(e.target.value)} disabled={!isSuper} className="h-9 w-24 rounded-full text-center tabular-nums" aria-label="Jours d'inactivité avant suppression" data-testid="autodelete-days" />
        <span className="text-sm">jours d'inactivité</span>
        <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="autodelete-hint">
          {n === 0 ? "0 = jamais (infini)" : `→ avertissement à ${warnAt} j, suppression à ${n} j`}
        </span>
        {isSuper && <Button size="sm" className="pill-primary rounded-full h-9 ml-auto" onClick={save} disabled={!dirty || busy === "save"} data-testid="autodelete-save">{busy === "save" ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Enregistrer</Button>}
      </div>
      <div className="mt-3 rounded-xl p-3 text-xs" style={{ background: "var(--yalola-bg)", color: "var(--yalola-text-muted)" }} data-testid="autodelete-rules">
        <b>Jamais supprimés automatiquement :</b> admins, associés et rôles de gestion · comptes vérifiés (badge ✓) · créateurs avec un solde à verser ou un retrait en cours · comptes dont la suppression est déjà programmée.
        {" "}Un membre qui se reconnecte après l'avertissement est aussitôt sorti de la liste. Garde-fous : 500 avertissements et 100 suppressions max par passage ; passage annulé si plus de 20 % des membres devaient disparaître d'un coup.
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="autodelete-last">
          <b>Dernier passage :</b> {config.last_sweep ? `${fmtWhen(config.last_sweep.at)} — ${sweepSummary(config.last_sweep)}` : sweepSummary(null)}
        </span>
        {isSuper && <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => sweep(true)} disabled={!!busy} data-testid="autodelete-preview">{busy === "preview" ? <Loader2 size={13} className="animate-spin mr-1" /> : <Eye size={13} className="mr-1" />} Aperçu du prochain passage</Button>
          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => sweep(false)} disabled={!!busy || !config.enabled || !(config.auto_delete_days > 0)} data-testid="autodelete-run">{busy === "run" ? <Loader2 size={13} className="animate-spin mr-1" /> : <Play size={13} className="mr-1" />} Lancer maintenant</Button>
        </div>}
      </div>
      {preview && (
        <div className="mt-2 rounded-xl px-3 py-2 text-xs" style={{ background: preview.dry ? "var(--yalola-primary-soft)" : "#D1FAE5", color: preview.dry ? "var(--yalola-primary)" : "#065F46" }} data-testid="autodelete-preview-result">
          {preview.dry ? "Aperçu (rien envoyé, rien supprimé) : " : "Passage effectué : "}{sweepSummary(preview)}
          {preview.dry && !preview.disabled && !preview.aborted && preview.warned > 0 && ` — les ${preview.warned} avertis seraient supprimés dans ${config.warn_days || 7} jours sans reconnexion.`}
        </div>
      )}
    </div>
  );
};
